import { useEffect, useState } from 'react';
import { fetchState } from './lib/api';
import { getDeviceId, hasConsent, resetLocalIdentity, setConsent } from './lib/device';
import { useLedgerStore } from './store';
import { TurnstileGate } from './components/TurnstileGate';
import { TrustGame } from './components/TrustGame';
import { CommonsMap } from './components/CommonsMap';
import { ProfilePanel } from './components/ProfilePanel';

function pct(value?: number): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

function ConsentModal({ onAccept }: { onAccept: () => void }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <span className="kicker">one-line consent</span>
        <h2>Play only if this feels fair.</h2>
        <p className="lede">By playing, you consent to anonymous device-keyed trust decisions being saved in D1 and shown only as aggregate commons signals; no names, emails, or tracking cookies.</p>
        <div className="actions">
          <button className="primary" onClick={onAccept}>I understand; enter the bridge</button>
        </div>
      </div>
    </div>
  );
}

export function App() {
  const deviceId = useLedgerStore((store) => store.deviceId);
  const state = useLedgerStore((store) => store.state);
  const commons = useLedgerStore((store) => store.commons);
  const view = useLedgerStore((store) => store.view);
  const toast = useLedgerStore((store) => store.toast);
  const setDeviceId = useLedgerStore((store) => store.setDeviceId);
  const setState = useLedgerStore((store) => store.setState);
  const setView = useLedgerStore((store) => store.setView);
  const setLoading = useLedgerStore((store) => store.setLoading);
  const setToast = useLedgerStore((store) => store.setToast);
  const loading = useLedgerStore((store) => store.loading);
  const [consented, setConsented] = useState(() => hasConsent());

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      const id = await getDeviceId();
      if (cancelled) return;
      setDeviceId(id);
      if (hasConsent()) {
        setLoading(true);
        try {
          const next = await fetchState(id);
          if (!cancelled) setState(next);
        } catch (error) {
          if (!cancelled) setToast(error instanceof Error ? error.message : 'Could not load state');
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
    }
    boot();
    return () => { cancelled = true; };
  }, [setDeviceId, setLoading, setState, setToast]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(undefined), 4200);
    return () => window.clearTimeout(timer);
  }, [toast, setToast]);

  async function acceptConsent() {
    setConsent(true);
    setConsented(true);
    const id = deviceId || await getDeviceId();
    setDeviceId(id);
    setLoading(true);
    try {
      setState(await fetchState(id));
    } catch (error) {
      setToast(error instanceof Error ? error.message : 'Could not load state');
    } finally {
      setLoading(false);
    }
  }

  function resetIdentity() {
    resetLocalIdentity();
    window.location.reload();
  }

  return (
    <main className="app-shell">
      {!consented ? <ConsentModal onAccept={acceptConsent} /> : null}

      <section className="hero">
        <div className="hero-card">
          <span className="kicker"><span className="dot" /> betrayal aversion · reciprocity · trust repair</span>
          <h1>Stranger’s Ledger</h1>
          <p className="lede">A small, sharp game about the moment trust leaves your hand. Send coins to a stranger, watch them triple, and wait to learn whether the bridge holds.</p>
          <div className="consent-line">Consent: anonymous device-keyed cooperation data is stored; aggregates feed a public commons; you can reset this device anytime.</div>
          <div className="creator-links" aria-label="Creator links">
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer">LinkedIn</a>
            <a className="coffee" href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer">Buy me a coffee</a>
          </div>
        </div>
        <aside className="hero-side panel">
          <span className="status-pill"><span className="dot" /> {loading ? 'syncing D1…' : 'Cloudflare free-tier stack'}</span>
          <TurnstileGate />
          <div className="stat-grid">
            <div className="stat"><strong>{commons?.roundsTotal ?? 0}</strong><span>human rounds</span></div>
            <div className="stat"><strong>{pct(commons?.avgSentPct)}</strong><span>commons trust</span></div>
            <div className="stat"><strong>{pct(commons?.breakRate)}</strong><span>break rate</span></div>
            <div className="stat"><strong>{pct(commons?.repairRate)}</strong><span>repair pulse</span></div>
          </div>
        </aside>
      </section>

      <nav className="nav" aria-label="Primary">
        <button className={view === 'play' ? 'active' : ''} onClick={() => setView('play')}>Play</button>
        <button className={view === 'profile' ? 'active' : ''} onClick={() => setView('profile')}>Self-knowledge</button>
        <button className={view === 'commons' ? 'active' : ''} onClick={() => setView('commons')}>Commons map</button>
        <button className={view === 'help' ? 'active' : ''} onClick={() => setView('help')}>Help / methods</button>
      </nav>

      {view === 'play' ? <TrustGame /> : null}
      {view === 'profile' ? <ProfilePanel stats={state?.profile.stats} /> : null}
      {view === 'commons' ? (
        <section className="grid">
          <div className="panel">
            <h2>The commons map</h2>
            <p className="muted">Each node is a scene. Horizontal means more trust extended; vertical means more reciprocity returned. Bigger nodes have more human rounds.</p>
            <CommonsMap commons={commons} />
          </div>
          <div className="panel">
            <h3>Recent semantic signals</h3>
            <p className="muted">Workers AI embeds structured event summaries with BGE. Vectors are stored as JSON in D1; nearest-neighbor comparisons use cosine in the Worker—no Vectorize.</p>
            <div className="cards">
              {(commons?.recentSignals ?? []).map((signal) => (
                <div className="card" key={signal.id}>
                  <div className="card-header"><strong>{signal.kind}</strong><span className="badge">{new Date(signal.createdAt).toLocaleDateString()}</span></div>
                  <p className="micro">{signal.summary}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}
      {view === 'help' ? (
        <section className="grid">
          <div className="panel">
            <h2>What this measures</h2>
            <p className="muted">The core is the Berg–Dickhaut–McCabe Trust Game: one player sends part of an endowment; the transfer is multiplied; the receiver chooses what to return. This version adds a betrayal-aversion proxy through prediction, response latency, and repair behavior.</p>
            <div className="card"><h3>Human positive</h3><p className="muted">Players get self-knowledge: trust extended, reciprocity, prediction error, breach history, and repair tendency. The aggregate map is public-facing by design.</p></div>
            <div className="card"><h3>Anonymous by construction</h3><p className="muted">Identity is a random local device key hashed in the browser. The Worker never asks for a name, email, IP-derived profile, or social login.</p></div>
          </div>
          <div className="panel">
            <h2>Data controls</h2>
            <p className="muted">This resets the local device key. It does not delete already-aggregated anonymous rounds from D1; those are part of the commons dataset.</p>
            <button className="danger-soft" onClick={resetIdentity}>Reset this device identity</button>
          </div>
        </section>
      ) : null}

      <footer className="footer">
        <span>Built for Cloudflare Pages + Functions + D1 + Workers AI + Turnstile. No Vectorize, Queues, Browser Rendering, ads, or paid services.</span>
        <span>Device: {deviceId ? `${deviceId.slice(0, 8)}…` : 'initializing'}</span>
      </footer>

      {toast ? <div className="toast" role="status">{toast}</div> : null}
    </main>
  );
}
